import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTION_NAME = 'registrations';

/**
 * Create a new event registration
 * @param {Object} registrationData - Registration data
 * @returns {Promise<string>} Registration ID
 */
export const createRegistration = async (registrationData) => {
  try {
    const registration = {
      ...registrationData,
      status: 'registered',
      registeredAt: Timestamp.now(),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), registration);
    return docRef.id;
  } catch (error) {
    console.error('Error creating registration:', error);
    throw new Error('Failed to create registration: ' + error.message);
  }
};

/**
 * Get registration by ID
 * @param {string} registrationId - Registration ID
 * @returns {Promise<Object|null>} Registration data
 */
export const getRegistrationById = async (registrationId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, registrationId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        registrationId: docSnap.id,
        ...docSnap.data(),
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting registration:', error);
    throw new Error('Failed to get registration: ' + error.message);
  }
};

/**
 * Get all registrations for a volunteer
 * @param {string} volunteerId - Volunteer user ID
 * @returns {Promise<Array>} Array of registrations
 */
export const getRegistrationsByVolunteer = async (volunteerId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('volunteerId', '==', volunteerId)
    );

    const querySnapshot = await getDocs(q);
    const registrations = [];

    querySnapshot.forEach((doc) => {
      registrations.push({
        registrationId: doc.id,
        ...doc.data(),
      });
    });

    // Sort by event date in memory (descending - newest first)
    registrations.sort((a, b) => {
      const timeA = a.eventDate?.toMillis?.() || 0;
      const timeB = b.eventDate?.toMillis?.() || 0;
      return timeB - timeA;
    });

    return registrations;
  } catch (error) {
    console.error('Error getting registrations by volunteer:', error);
    throw new Error('Failed to get registrations: ' + error.message);
  }
};

/**
 * Get upcoming registrations for a volunteer
 * @param {string} volunteerId - Volunteer user ID
 * @returns {Promise<Array>} Array of upcoming registrations
 */
export const getUpcomingRegistrationsByVolunteer = async (volunteerId) => {
  try {
    const now = Timestamp.now();
    const q = query(
      collection(db, COLLECTION_NAME),
      where('volunteerId', '==', volunteerId),
      where('status', '==', 'registered'),
      where('eventDate', '>=', now),
      orderBy('eventDate', 'asc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ registrationId: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting upcoming registrations:', error);
    throw new Error('Failed to get upcoming registrations: ' + error.message);
  }
};

/**
 * Get past registrations for a volunteer
 * @param {string} volunteerId - Volunteer user ID
 * @returns {Promise<Array>} Array of past registrations
 */
export const getPastRegistrationsByVolunteer = async (volunteerId) => {
  try {
    const now = Timestamp.now();
    const q = query(
      collection(db, COLLECTION_NAME),
      where('volunteerId', '==', volunteerId),
      where('eventDate', '<', now),
      orderBy('eventDate', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ registrationId: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting past registrations:', error);
    throw new Error('Failed to get past registrations: ' + error.message);
  }
};

/**
 * Get all registrations for an event
 * @param {string} eventId - Event ID
 * @param {string} organizationId - Optional organization ID to filter by
 * @returns {Promise<Array>} Array of registrations
 */
export const getRegistrationsByEvent = async (eventId, organizationId = null) => {
  try {
    let q = query(
      collection(db, COLLECTION_NAME),
      where('eventId', '==', eventId)
    );

    // Add organization filter if provided (helps with Firestore security rules)
    if (organizationId) {
      q = query(q, where('organizationId', '==', organizationId));
    }

    const querySnapshot = await getDocs(q);
    const registrations = [];

    querySnapshot.forEach((doc) => {
      registrations.push({
        registrationId: doc.id,
        ...doc.data(),
      });
    });

    // Sort by registration date (newest first)
    registrations.sort((a, b) => {
      const timeA = a.registeredAt?.toMillis?.() || 0;
      const timeB = b.registeredAt?.toMillis?.() || 0;
      return timeB - timeA;
    });

    return registrations;
  } catch (error) {
    console.error('Error getting registrations by event:', error);
    throw new Error('Failed to get registrations: ' + error.message);
  }
};

/**
 * Get registrations count for an event
 * @param {string} eventId - Event ID
 * @returns {Promise<number>} Number of active registrations
 */
export const getRegistrationsCountByEvent = async (eventId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('eventId', '==', eventId),
      where('status', '==', 'registered')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting registrations count:', error);
    throw new Error('Failed to get registrations count: ' + error.message);
  }
};

/**
 * Get all registrations for an organization
 * @param {string} organizationId - Organization ID
 * @param {Object} dateRange - Optional date range filter {start, end}
 * @returns {Promise<Array>} Array of registrations
 */
export const getRegistrationsByOrganization = async (organizationId, dateRange = null) => {
  try {
    let q = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId)
    );

    // Apply date range filter if provided
    if (dateRange?.start) {
      q = query(q, where('eventDate', '>=', dateRange.start));
    }
    if (dateRange?.end) {
      q = query(q, where('eventDate', '<=', dateRange.end));
    }

    const querySnapshot = await getDocs(q);
    const registrations = [];

    querySnapshot.forEach((doc) => {
      registrations.push({
        registrationId: doc.id,
        ...doc.data(),
      });
    });

    // Sort by event date (descending)
    registrations.sort((a, b) => {
      const timeA = a.eventDate?.toMillis?.() || 0;
      const timeB = b.eventDate?.toMillis?.() || 0;
      return timeB - timeA;
    });

    return registrations;
  } catch (error) {
    console.error('Error getting registrations by organization:', error);
    throw new Error('Failed to get registrations: ' + error.message);
  }
};

/**
 * Update registration status
 * @param {string} registrationId - Registration ID
 * @param {string} status - New status ('registered' | 'attended' | 'cancelled')
 * @returns {Promise<void>}
 */
export const updateRegistrationStatus = async (registrationId, status) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, registrationId);
    await updateDoc(docRef, {
      status,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating registration status:', error);
    throw new Error('Failed to update registration status: ' + error.message);
  }
};

/**
 * Cancel a registration
 * @param {string} registrationId - Registration ID
 * @param {string} [reason] - Optional cancellation reason
 * @returns {Promise<void>}
 */
export const cancelRegistration = async (registrationId, reason = '') => {
  try {
    const docRef = doc(db, COLLECTION_NAME, registrationId);
    const updates = {
      status: 'cancelled',
      updatedAt: Timestamp.now(),
    };
    if (reason) updates.cancellationReason = reason;
    await updateDoc(docRef, updates);
  } catch (error) {
    console.error('Error cancelling registration:', error);
    throw new Error('Failed to cancel registration: ' + error.message);
  }
};

/**
 * Cancel all upcoming registrations for a volunteer across a set of event IDs
 * Used for series cancellation from the volunteer's schedule page.
 * @param {string} volunteerId
 * @param {string[]} eventIds - IDs of events in the series to cancel
 * @param {string} assignmentName - Only cancel registrations for this assignment
 * @returns {Promise<number>} Number of registrations cancelled
 */
export const cancelVolunteerSeriesRegistrations = async (volunteerId, eventIds, assignmentName, reason = '') => {
  try {
    let cancelled = 0;
    // Process in sequence to avoid Firestore write conflicts
    for (const eventId of eventIds) {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('volunteerId', '==', volunteerId),
        where('eventId', '==', eventId),
        where('assignmentName', '==', assignmentName),
        where('status', '==', 'registered')
      );
      const snapshot = await getDocs(q);
      const updates = { status: 'cancelled', updatedAt: Timestamp.now() };
      if (reason) updates.cancellationReason = reason;
      await Promise.all(
        snapshot.docs.map((d) =>
          updateDoc(doc(db, COLLECTION_NAME, d.id), updates)
        )
      );
      cancelled += snapshot.size;
    }
    return cancelled;
  } catch (error) {
    console.error('Error cancelling series registrations:', error);
    throw new Error('Failed to cancel series registrations: ' + error.message);
  }
};

/**
 * Mark volunteer attendance
 * @param {string} registrationId - Registration ID
 * @param {boolean} attended - Whether volunteer attended
 * @param {number} hoursServed - Hours served (if attended)
 * @returns {Promise<void>}
 */
export const markAttendance = async (registrationId, attended, hoursServed = 0) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, registrationId);
    const updates = {
      status: attended ? 'attended' : 'no-show',
      updatedAt: Timestamp.now(),
    };

    if (attended && hoursServed > 0) {
      updates.hoursServed = hoursServed;
    }

    await updateDoc(docRef, updates);
  } catch (error) {
    console.error('Error marking attendance:', error);
    throw new Error('Failed to mark attendance: ' + error.message);
  }
};

/**
 * Delete a registration
 * @param {string} registrationId - Registration ID
 * @returns {Promise<void>}
 */
export const deleteRegistration = async (registrationId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, registrationId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting registration:', error);
    throw new Error('Failed to delete registration: ' + error.message);
  }
};

/**
 * Get all registrations for a volunteer for a specific event
 * @param {string} volunteerId - Volunteer user ID
 * @param {string} eventId - Event ID
 * @returns {Promise<Array>} Array of registrations
 */
export const getVolunteerRegistrationsForEvent = async (volunteerId, eventId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('volunteerId', '==', volunteerId),
      where('eventId', '==', eventId),
      where('status', '==', 'registered')
    );

    const querySnapshot = await getDocs(q);
    const registrations = [];

    querySnapshot.forEach((doc) => {
      registrations.push({
        registrationId: doc.id,
        ...doc.data(),
      });
    });

    return registrations;
  } catch (error) {
    console.error('Error getting volunteer registrations for event:', error);
    throw new Error('Failed to get registrations: ' + error.message);
  }
};

/**
 * Check if volunteer is already registered for a specific assignment
 * @param {string} volunteerId - Volunteer user ID
 * @param {string} eventId - Event ID
 * @param {string} assignmentName - Assignment name
 * @returns {Promise<Object|null>} Existing registration or null
 */
export const getExistingRegistration = async (volunteerId, eventId, assignmentName = null, organizationId = null) => {
  try {
    console.log('🔍 Querying for existing registration:', { volunteerId, eventId, assignmentName, status: 'registered' });

    let q = query(
      collection(db, COLLECTION_NAME),
      where('volunteerId', '==', volunteerId),
      where('eventId', '==', eventId),
      where('status', '==', 'registered')
    );

    if (assignmentName) {
      q = query(q, where('assignmentName', '==', assignmentName));
    }
    if (organizationId) {
      q = query(q, where('organizationId', '==', organizationId));
    }

    const querySnapshot = await getDocs(q);
    console.log('📊 Query results:', querySnapshot.size, 'registrations found');

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const registration = {
        registrationId: doc.id,
        ...doc.data(),
      };
      console.log('✅ Found existing registration:', registration);
      return registration;
    }

    console.log('✅ No existing registration found');
    return null;
  } catch (error) {
    console.error('❌ Error checking existing registration:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw new Error('Failed to check existing registration: ' + error.message);
  }
};
